import { useCallback, useEffect, useMemo, useState } from "react";
import apiClient from "../../Utils/apiClient";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import { Box, TextField } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-datetime/css/react-datetime.css";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import ImageModal from "components/ImageModal";
import EmptyData from "components/EmptyData";
import GoogleMapModal from "DialogBox/googlemap";
import { useMaterialUIController } from "context";
import { getTimeFromTimestamp } from "Utils/helper";

function ShortDistanceGSM() {
  const navigate = useNavigate();
  const { getUserState, allData } = useSelector((state) => state.data);
  const [controller] = useMaterialUIController();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [onClickImageData, setOnClickImageData] = useState("");
  const [openGmap, setOpenGmap] = useState(false);
  const [markerList, setMarkerList] = useState([]);
  const [userLocation, setUserLocation] = useState([]);

  const [checkSuperAdmin, setCheckSuperAdmin] = useState(null);
  const [adminState, setAdminState] = useState(null);

  const handleError = (msg) => toast.error(msg);

  const handleImageClick = useCallback((url) => {
    setIsModalOpen(true);
    setOnClickImageData(url);
  }, []);

  const handleStops = useCallback((stopData) => {
    const stops = Object.values(stopData);
    setUserLocation(stops);
    setMarkerList(stops.filter((d) => !d.is_moving));
    setOpenGmap(true);
  }, []);

  const closeModals = useCallback(() => {
    setIsModalOpen(false);
    setOpenGmap(false);
    setMarkerList([]);
    setUserLocation([]);
  }, []);

  const buildRow = (user, shortTrip) => ({
    Name: user.userName || "Unknown",
    UserImage: user?.userImage || user?.userSelfie ? (
      <img
        onClick={() => handleImageClick(user.userImage || user.userSelfie)}
        src={user.userImage || user.userSelfie}
        alt={user.userName || "user"}
        style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
      />
    ) : (
      "N/A"
    ),
    UserPhone: user?.userPhone || "N/A",
    VehicleImage: shortTrip?.imgVehicle || user?.userVehicle || user?.userImage ? (
      <img
        onClick={() => handleImageClick(shortTrip.imgVehicle || user?.userVehicle || user?.userImage)}
        src={shortTrip.imgVehicle || user?.userVehicle || user?.userImage}
        alt="vehicle"
        style={{ width: 50, height: 50, borderRadius: "50%", cursor: "pointer" }}
      />
    ) : (
      "N/A"
    ),
    State: user.state || "N/A",
    TripDate: shortTrip.ArrivalDate || "N/A",
    TripTime: getTimeFromTimestamp(shortTrip.currentTimeStamp) || "N/A",
    currentTimeStamp: shortTrip.currentTimeStamp,
    TripEndTime: getTimeFromTimestamp(shortTrip?.tripendtime) || "N/A",
    TripStatus: (
      <button
        style={{
          backgroundColor:
            shortTrip.Trip_Status === "Moving"
              ? (user.is_moving || user.IsMoving ? "green" : "orange")
              : shortTrip.Trip_Status === "Arrived"
              ? "yellow"
              : "red",
          color: shortTrip.Trip_Status === "Arrived" ? "black" : "white",
          padding: "6px 16px",
          border: "none",
          borderRadius: "4px",
          cursor: "pointer",
          fontWeight: "500",
        }}
      >
        {shortTrip.Trip_Status === "Moving"
          ? (user.is_moving || user.IsMoving ? "Running (Moving)" : "Running (Stationary)")
          : shortTrip.Trip_Status === "Arrived"
          ? "Arrived"
          : "Trip Ended"}
      </button>
    ),
  });

  const getData = useCallback(async () => {
    const adminData = JSON.parse(localStorage.getItem("user"));
    if (!adminData) return navigate("/authentication/sign-in");

    const userState = adminData.state;
    const isSuperAdmin = adminData.superAdmin;
    setCheckSuperAdmin(isSuperAdmin);
    setAdminState(userState);

    setLoading(true);
    try {
      const response = await apiClient.get("/users/short-escorts?gsmMode=true");
      console.log("[ShortDistanceGSM] API response for short-escorts (gsmMode=true):", response?.data);
      const escortUsers = Array.isArray(response?.data)
        ? response.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

      console.log("[ShortDistanceGSM] short-escorts API response:", response?.data);
      console.log("[ShortDistanceGSM] short-escorts users count:", escortUsers.length);

      if (!escortUsers.length) {
        setRows([]);
        return;
      }

      const newRows = [];

      escortUsers.forEach((user) => {
        const shortEscorts = Array.isArray(user?.ShortDistance_Escort)
          ? user.ShortDistance_Escort
          : Object.values(user?.ShortDistance_Escort || {});
        const { state } = user;
        const rawGsm = user?.gsmMode ?? user?.userData?.gsmMode;
        const isGsmMode = rawGsm === true || rawGsm === "true" || rawGsm === 1;

        const allowed =
          (isSuperAdmin && (!getUserState || getUserState === "All State")) ||
          (isSuperAdmin && state === getUserState) ||
          (!isSuperAdmin && state === userState);

        if (!allowed || !isGsmMode) return;

        shortEscorts.forEach((shortTrip) => {
          const isTripGsm = shortTrip?.gsmMode === true || shortTrip?.gsmMode === "true" || isGsmMode;
          if (!isTripGsm) return;

          console.log("[ShortDistanceGSM] short escort row data:", {
            userId: user?.userId || user?._id || null,
            state: user?.state || "N/A",
            gsmMode: isGsmMode,
            shortTrip,
          });

          newRows.push(buildRow(user, shortTrip));
        });
      });

      const sortedRows = newRows
        .sort((a, b) => b.currentTimeStamp - a.currentTimeStamp)
        .map((row, index) => ({
          ...row,
          Sr: index + 1,
        }));

      setRows(sortedRows);
    } catch (err) {
      handleError(err?.message || "Error fetching escort data.");
    } finally {
      setLoading(false);
    }
  }, [getUserState, navigate]);

  useEffect(() => {
    getData();
  }, [getData, allData, getUserState]);

  const filteredRows = useMemo(() => {
    const search = searchTerm.toLowerCase().trim();

    return rows.filter(
      (r) =>
        String(r.Name).toLowerCase().includes(search) ||
        String(r.UserPhone).toLowerCase().includes(search)
    );
  }, [rows, searchTerm]);

  return (
    <DashboardLayout>
      <MDBox pt={6} pb={3}>
        <Box display="flex" justifyContent="flex-end" mb={5} px={2}>
          <TextField
            label="Search by name or mobile"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ width: 300 }}
          />
        </Box>

        <Grid container spacing={6} style={{ position: "relative", minHeight: "60vh" }}>
          {loading ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              Loading...
            </Box>
          ) : (
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="info"
                  borderRadius="lg"
                  coloredShadow="info"
                >
                  <ToastContainer />
                  <MDTypography variant="h6" color="white">
                    {checkSuperAdmin
                      ? getUserState === "" || getUserState === "All State"
                        ? "All States"
                        : `${getUserState} State`
                      : `${adminState} State`}{" "}
                    Short Distance Escort (GSM Mode)
                  </MDTypography>
                </MDBox>

                <MDBox pt={3}>
                  {filteredRows.length ? (
                    <DataTable
                      table={{
                        columns: [
                          { Header: "Sr", accessor: "Sr" },
                          { Header: "User Image", accessor: "UserImage" },
                          { Header: "Name", accessor: "Name" },
                          { Header: "User Phone", accessor: "UserPhone" },
                          { Header: "Vehicle Image", accessor: "VehicleImage" },
                          { Header: "State", accessor: "State" },
                          { Header: "Trip Date", accessor: "TripDate" },
                          { Header: "Start Trip Time", accessor: "TripTime" },
                          { Header: "Trip Status", accessor: "TripStatus" },
                          { Header: "End Trip Time", accessor: "TripEndTime" },
                        ],
                        rows: filteredRows,
                      }}
                      isSorted={false}
                      entriesPerPage={false}
                      showTotalEntries={false}
                      noEndBorder
                    />
                  ) : (
                    <EmptyData />
                  )}
                </MDBox>
              </Card>

              <ImageModal
                onClickImageData={onClickImageData}
                onClose={() => setIsModalOpen(false)}
                isModalOpen={isModalOpen}
              />
              {openGmap && (
                <GoogleMapModal
                  open={openGmap}
                  onClose={closeModals}
                  markerList={markerList}
                  userLocation={userLocation}
                />
              )}
            </Grid>
          )}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}

export default ShortDistanceGSM;
